import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';

/**
 * `TransitionGroup` is a component for managing component states (including mounting and unmounting) over time, specifically
 * designed with animation in mind. It is built on top of
 * [`react-transition-group`](https://github.com/reactjs/react-transition-group), but offers a subset of its functionality
 * and a performance enhancement in form of the *contentRenderer* prop.
 *
 * `TransitionGroup` does not render a DOM element by itself, but will render a single child component when *in* is set to
 * `true`.
 *
 * `TransitionGroup` can render its child in two ways:
 * 1. The standard react-transition-group way by adding your component as child of the `TransitionGroup`. The drawback of
 * this method is that React will create the child every time the `TransitionGroup` is rendered, but it will only be mounted
 * in the DOM model when *in* is set to `true`. Use this for simple child components that have no impact on performance.
 * 2. Use the *contentRenderer* prop to render the child. React will not create the child until it is needed. Use this for
 * advanced child elements that may have impact on performance.
 *
 * vimond-core-components comes with the following transitions built in:
 * - "animate-expanded-content" for animating collapsing and expanding content. This transition is used by {@link Accordion}.
 * - "animate-dialog" for animating showing and hiding dialogs.
 * - "animate-input-options" for animating showing and hiding input options. This transition is used by {@link Input}.
 * - "animate-lightbox" for animating lightbox transitions.
 * - "animate-snackbar" for animating snackbar transitions. This transition is used by {@link Snackbar}.
 *
 * Transition duration is set to 250ms by default so your animations should be completed within 250ms. The default delay used
 * in this example is 1000ms to make it easier to see the effect.
 *
 * Props not listed here will be forwared to the react-transition-group component.
 *
 * @property {any} children A single child component that will be animated. The child will only be rendered if *in* is
 * `true`.
 * @property {string} className The name of the transition animation to use. Required. See
 * [`react-transition-group`](https://github.com/reactjs/react-transition-group) or the styling for e.g. {@link Lightbox} on
 * how to define your own transitions.
 * @property {boolean} enter Animates the child when *in* is set to `true`. Defaults to `true`.
 * @property {boolean} exit Animates the child when *in* is set to `false`. Defaults to `true`.
 * @property {boolean} in Sets the animation state of the `TransitionGroup`. For instance, if you have a state `showDialog`
 * that should show or hide a dialog in your app, pass this state to the *in* prop to trigger the animation.
 * @property {number} timeout The duration of the transition, in milliseconds. Defaults to 250. Animations should rarely be
 * longer than 250 milliseconds.
 *
 * @see Snackbar
 */

TransitionGroup.propTypes = {
  className: PropTypes.string,
  enter: PropTypes.bool,
  exit: PropTypes.bool,
  in: PropTypes.bool,
  contentRenderer: PropTypes.func,
  timeout: PropTypes.number,
};

TransitionGroup.defaultProps = {
  enter: true,
  exit: true,
  mountOnEnter: true,
  timeout: 250,
  unmountOnExit: true,
};

function TransitionGroup(props) {
  const { children, className, contentRenderer, in: isIn, ...rest } = props;
  const [renderContent, setRenderContent] = useState(isIn);
  const content = children || (renderContent && contentRenderer ? contentRenderer() : <span />);

  useEffect(() => {
    if (isIn && !renderContent) setRenderContent(true);
  }, [isIn]);

  const onExited = () => setRenderContent(false);

  return (
    <CSSTransition classNames={className} in={isIn} onExited={onExited} {...rest}>
      {content}
    </CSSTransition>
  );
}

export default TransitionGroup;
